import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import validations from 'utils/validations';
import {
  documentCalculation,
  extractData,
  formDescription,
  formDocumentCalculation,
  GQLGetOrderPartialProperties,
} from './services';
import useAlert from 'utils/alert';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { addManualCharge } from 'components/add-entry-modal/services/mutations';
import { capitalize } from 'utils/helpers';
import { useEffect, useState } from 'react';
import { getOrderPartialProperties } from './services/queries';
import { editManualCharge } from 'graphql/ledger/mutations';

// .passthrough(): handleEntry reads currentTDI/numberOfPages/numberOfNames/
// letter/amount off the parsed data too — fields this schema doesn't
// validate, which zod would otherwise strip.
export const validationSchema = z.object({
  code: validations.requiredString('Required'),
  description: validations.requiredString('Required'),
  kind: validations.requiredString('Required'),
}).passthrough();

const useDocumentFeesModal = ({
  onClose,
  letter,
  documentAccountingId = null,
  documentDescription = null,
}: {
	onClose: () => void;
	letter: string;
	documentAccountingId?: string | null;
	documentDescription?: string | null;
}) => {
  const { id: orderId } = useParams<{ id: string }>();
  const [createErrors, setcreateErrors] = useState<string[]>([]);
  const showAlert = useAlert();
  const { t } = useTranslation();
  const resolver = zodResolver(validationSchema);

  const methods = useForm<formDocumentCalculation>({
    defaultValues: {
      numberOfNames: 0,
      numberOfPages: 0,
      code: 'document',
      kind: 'manual',
      letter,
      description: '',
    },
    resolver,
  });

  useEffect(() => {
    const { reset } = methods;
    if (documentDescription) {
      const dataExtracted = extractData(documentDescription);
      reset({
        numberOfNames: dataExtracted?.names || 0,
        numberOfPages: dataExtracted?.pages || 0,
        code: 'document',
        kind: 'manual',
        letter,
        description: dataExtracted?.document || '',
        currentTDI:
					documentCalculation.find((f) => f.countyName.includes(dataExtracted?.county || '')) ||
					null,
      });
    }
    // methods/reset are unstable; adding them would re-run this effect on every form render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentDescription, letter]);

  useQuery<GQLGetOrderPartialProperties>(
    ['order-properties-partial', orderId],
    () => getOrderPartialProperties({ id: orderId }),
    {
      refetchOnWindowFocus: false,
      onSuccess: (localData) => {
        const { setValue } = methods;
        if (localData?.getOrder.estates.length > 0) {
          if (!documentAccountingId) {
            const currentDoc = documentCalculation.find((f) =>
              f.countyName.includes(localData?.getOrder.estates?.[0].address.settlement || '')
            );
            setValue('currentTDI', currentDoc || null);
          }
        }
      },
    }
  );

  const { mutate: AddManualcharge, ...responseAddManualcharge } = useMutation(addManualCharge, {
    onSuccess: (data) => {
      if (data) {
        showAlert(capitalize(t('dialogs:order-updated')), 'success');
        responseAddManualcharge.reset();
        methods.reset();
        onClose();
      }
    },
    onError: (e: any) => {
      const { errors = ['Error Adding Association'] } = e?.response || {};
      const errorsMapped = errors.map((error: any) => error.message);
      setcreateErrors(errorsMapped);
    },
  });

  const { mutate: EditManualcharge, ...responseEditManualcharge } = useMutation(editManualCharge, {
    onSuccess: (data) => {
      if (data) {
        showAlert(capitalize(t('dialogs:order-updated')), 'success');
        responseEditManualcharge.reset();
        setcreateErrors([]);
        methods.reset();
        onClose();
      }
    },
    onError: (e: any) => {
      const { errors = ['Error Adding Association'] } = e?.response || {};
      const errorsMapped = errors.map((error: any) => error.message);
      setcreateErrors(errorsMapped);
    },
  });

  const handleEntry = methods.handleSubmit((data) => {
    const exampleDocDescription = formDescription({
      document: data.description,
      county: data.currentTDI?.countyName || '',
      pages: data.numberOfPages,
      names: data.numberOfNames,
    });
    if (documentAccountingId) {
      EditManualcharge({
        id: orderId!,
        accountingId: documentAccountingId,
        data: {
          description: exampleDocDescription || '',
          number: null,
          amount_override: parseFloat(data.amount as unknown as string) || 0,
          months: null,
          letter: data.letter === '' ? null : data.letter,
          months_advanced: null,
          annuality: null,
        },
      });
    } else {
      AddManualcharge({
        id: orderId!,
        description: exampleDocDescription || '',
        code: data.code,
        kind: data.kind,
        number: null,
        amount: parseFloat(data.amount as unknown as string) || 0,
        months: null,
        entryDate: null,
        letter: data.letter === '' ? null : data.letter,
        months_advanced: null,
        annuality: null,
        filters: undefined,
      });
    }
  });
  return {
    methods,
    handleEntry,
    responseAddManualcharge,
    createErrors,
  };
};

export default useDocumentFeesModal;
